import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getQuotations, updateQuotationStatus, convertQuotationToInvoice, deleteQuotation, getQuotationPdf } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Plus, MoreHorizontal, FileText, CheckCircle, XCircle, Trash2, Eye, Download, ArrowRightCircle } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency, formatDate } from '@/lib/utils';

const statusColors = {
  'Draft': 'bg-gray-100 text-gray-700 ring-1 ring-gray-300',
  'Sent': 'bg-blue-100 text-blue-700 ring-1 ring-blue-300',
  'Accepted': 'bg-green-100 text-green-700 ring-1 ring-green-300',
  'Rejected': 'bg-red-100 text-red-700 ring-1 ring-red-300',
  'Expired': 'bg-amber-100 text-amber-700 ring-1 ring-amber-300',
  'Converted': 'bg-purple-100 text-purple-700 ring-1 ring-purple-300'
};

export default function QuotationsPage() {
  const navigate = useNavigate();
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    loadQuotations();
  }, []);

  const loadQuotations = async () => {
    try {
      const res = await getQuotations({ limit: 100 });
      setQuotations(res.data.items);
      setTotal(res.data.total);
    } catch (error) {
      toast.error('Failed to load quotations');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await updateQuotationStatus(id, status);
      toast.success(`Status updated to ${status}`);
      loadQuotations();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleConvert = async (id) => {
    try {
      const res = await convertQuotationToInvoice(id);
      toast.success(`Converted to Invoice ${res.data.invoice_number}`);
      navigate(`/invoices/${res.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to convert');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this quotation?')) return;
    try {
      await deleteQuotation(id);
      toast.success('Quotation deleted');
      loadQuotations();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleDownloadPdf = async (id, quoteNumber) => {
    setDownloading(id);
    try {
      const res = await getQuotationPdf(id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${quoteNumber || 'quotation'}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to download PDF');
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="quotations-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Quotations</h1>
          <p className="text-muted-foreground">Create and manage quotations</p>
        </div>
        <Button onClick={() => navigate('/quotations/new')} data-testid="new-quotation-btn">
          <Plus className="w-4 h-4 mr-2" />
          New Quotation
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : quotations.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-20" />
              <p>No quotations yet</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Quote #</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Valid Until</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {quotations.map((q) => (
                  <TableRow key={q.id} data-testid={`quotation-row-${q.id}`}>
                    <TableCell className="font-mono text-sm">{q.quote_number}</TableCell>
                    <TableCell className="font-medium">{q.client_name}</TableCell>
                    <TableCell>{formatDate(q.quote_date)}</TableCell>
                    <TableCell>{formatDate(q.valid_until || q.expiry_date)}</TableCell>
                    <TableCell className="text-right font-mono font-semibold">
                      {formatCurrency(q.total_amount)}
                    </TableCell>
                    <TableCell>
                      <Badge className={statusColors[q.status] || 'bg-gray-100 text-gray-700'}>
                        {q.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {/* Quick Convert Button for Accepted/Sent quotations */}
                        {(q.status === 'Accepted' || q.status === 'Sent') && !q.converted_invoice_id && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleConvert(q.id)}
                            className="text-primary hover:text-primary"
                            data-testid={`convert-quote-${q.id}`}
                          >
                            <ArrowRightCircle className="w-4 h-4 mr-1" />
                            Convert
                          </Button>
                        )}
                        
                        {/* Quick Download PDF Button */}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDownloadPdf(q.id, q.quote_number)}
                          disabled={downloading === q.id}
                          data-testid={`download-quote-pdf-${q.id}`}
                        >
                          <Download className={`w-4 h-4 ${downloading === q.id ? 'animate-pulse' : ''}`} />
                        </Button>
                        
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`quote-actions-${q.id}`}>
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(`/quotations/${q.id}`)}>
                              <Eye className="w-4 h-4 mr-2" /> View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDownloadPdf(q.id, q.quote_number)}>
                              <Download className="w-4 h-4 mr-2" /> Download PDF
                            </DropdownMenuItem>
                            
                            <DropdownMenuSeparator />
                            
                            {q.status === 'Draft' && (
                              <DropdownMenuItem onClick={() => handleStatusChange(q.id, 'Sent')}>
                                <FileText className="w-4 h-4 mr-2" /> Mark as Sent
                              </DropdownMenuItem>
                            )}
                            {(q.status === 'Sent' || q.status === 'Draft') && (
                              <>
                                <DropdownMenuItem onClick={() => handleStatusChange(q.id, 'Accepted')}>
                                  <CheckCircle className="w-4 h-4 mr-2" /> Accept
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleStatusChange(q.id, 'Rejected')}>
                                  <XCircle className="w-4 h-4 mr-2" /> Reject
                                </DropdownMenuItem>
                              </>
                            )}
                            {(q.status === 'Accepted' || q.status === 'Sent') && !q.converted_invoice_id && (
                              <DropdownMenuItem onClick={() => handleConvert(q.id)}>
                                <ArrowRightCircle className="w-4 h-4 mr-2" /> Convert to Invoice
                              </DropdownMenuItem>
                            )}
                            
                            <DropdownMenuSeparator />
                            
                            {q.status === 'Draft' && (
                              <DropdownMenuItem onClick={() => handleDelete(q.id)} className="text-red-600">
                                <Trash2 className="w-4 h-4 mr-2" /> Delete
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <div className="mt-4 text-sm text-muted-foreground">
            Showing {quotations.length} of {total} quotations
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
